import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import Title from 'components/Title';
import { Col, Row, Card, Button, ListGroup, ButtonToolbar, ButtonGroup } from 'react-bootstrap';
import { Table } from 'components/Table';
import { NewGroupPopup } from './Popups/NewGroupPopup';
import { createStoreGroup, deleteStoreGroup, deleteStoresByGroup, updateStoreGroup, updateStoreGroupStores } from 'redux/slices/StoreGroups';
import { showToast } from 'redux/slices/Toasts';
import { RootState } from 'redux/store';
import { getManageGroupsStores, getManageGroupsStoreTree } from 'redux/slices/ManageGroups';
import Spinner from 'components/Spinner';
import Popup from 'components/Modal';
import { updateStoreGroupField, updateStoreGroupId } from 'redux/slices/Global';
import { getStores } from 'redux/slices/Stores';
import { TreeStoresPopup } from './Popups/TreeStoresPopup';
import { StoreGroupTree } from 'models/dbModels/other/StoreGroupTree';
import { StoreGroup } from 'models/dbModels/storeGroups/StoreGroup';
import { Store } from 'models/dbModels/stores/Store';

export function ManageGroups() {
	const dispatch = useAppDispatch();
	const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);
	const stores = useAppSelector((state: RootState) => state.stores);
	const manageGroups = useAppSelector((state: RootState) => state.manageGroups);
	const [selectedNode, setSelectedNode] = useState<StoreGroupTree | undefined>();
	const [addEditNode, setAddEditNode] = useState<StoreGroupTree | undefined>();
	const [manageGroupsStores, setManageGroupsStores] = useState<Store[]>([]);
	const [parentStores, setParentStores] = useState([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [showNewPopup, setShowNewPopup] = useState<boolean>(false);
	const [showEditPopup, setShowEditPopup] = useState<boolean>(false);
	const [storesPopup, setStoresPopup] = useState<boolean>(false);
	const selectedStores = useRef<Store[]>([]);
	const [displayPopup, setDisplayPopup] = useState<boolean>(false);
	const user = useAppSelector((state: RootState) => state.logedinUser);

	const [popupMessage, setPopupMessage] = useState<{
		title: string,
		description: string,
		buttonText: string,
		cancel: () => void,
		submit: () => void,
	}>();
	const columnsb = [
		{
			Header: 'Name',
			accessor: 'name',
			width: 1000,
			defaultCanSort: true,
		},
	];

	useEffect(() => {
		setLoading(true);
		dispatch(getManageGroupsStores({ keyword: manageGroups.keyword, offset: 0, }));
		dispatch(getManageGroupsStoreTree());
		dispatch(getStores({}));
		setLoading(false);
	}, []);

	useEffect(() => {
		let id = selectedNode?.id;
		if (id) SelectNode(manageGroups.tree, id);
	}, [manageGroups.tree]);

	useEffect(() => {
		setManageGroupsStores(stores.table.filter((i: any) => selectedNode?.stores.includes(i.id)));
	}, [manageGroups.stores, selectedNode]);

	useEffect(() => {
		const parentId: any = selectedNode?.parentStoreGroupId;
		if (manageGroups.tree && parentId !== undefined) {
			const storesIds = getStoresByParentGroupId(manageGroups.tree[0], parentId);
			let storesWithData: any = [];
			stores.table.forEach((store: any) => {
				if (storesIds.includes(store.id)) storesWithData.push(store);
			});
			setParentStores(storesWithData);
		}
	}, [selectedNode]);

	function getStoresByParentGroupId(data: StoreGroupTree, parentGroupId: number): number[] {
		const stores: number[] = [];
		function traverseGroups(group: StoreGroupTree) {
			if (group.id === parentGroupId || parentGroupId === 1) stores.push(...group.stores);
			if (group.children && group.children.length > 0) {
				for (const childGroup of group.children) traverseGroups(childGroup);
			}
		}
		traverseGroups(data);
		return stores;
	}


	function RenderTree(nodes: StoreGroupTree[], depth: number): any[] {
		let result: any[] = [];
		nodes.forEach((n, index: number) => {
			result.push(
				<ListGroup.Item key={index} action onClick={() => setSelectedNode(n)} as={"div"} className='btn managed-groups-list-item' active={n === selectedNode}>
					<div style={{ display: 'flex', flex: 1 }}>
						<div style={{ marginLeft: (depth * 20) + 'px', marginTop: 'auto', marginBottom: 'auto' }}>
							{n.name}
						</div>

						<div style={{ flex: 1 }}></div>

						<div>
							<ButtonToolbar aria-label="Toolbar with button groups">
								<ButtonGroup size={"sm"}>
									<Button variant='light' className={user.permissions.storeGroups.canCreate ? '' : 'd-none'} onClick={() => {
										setAddEditNode(n);
										setShowNewPopup(true)
									}}>
										<i className="mdi mdi-plus mdi-18px"></i>
									</Button>
									<Button variant='light' className={user.permissions.storeGroups.canUpdate ? '' : 'd-none'} onClick={() => {
										setAddEditNode(n);
										setShowEditPopup(true)
									}}>
										<i className="mdi mdi-pencil"></i>
									</Button>
									<Button variant='light' className={user.permissions.storeGroups.canDelete ? '' : 'd-none'} disabled={depth === 0} onClick={() => {
										setAddEditNode(n);
										setPopupMessage({
											title: "Delete Group?",
											description: "Are you sure you wish to delete group?",
											buttonText: 'Delete',
											cancel: () => setDisplayPopup(false),
											submit: () => {
												DeleteNode(n);
												setDisplayPopup(false);
											}
										});
										setDisplayPopup(true);
									}}>
										<i className="mdi mdi-trash-can-outline"></i>
									</Button>
								</ButtonGroup>
							</ButtonToolbar>
						</div>
					</div>
				</ListGroup.Item>
			)

			if (n.children && n.children.length > 0) {
				result.push(RenderTree(n.children, depth + 1));
			}
		})

		return result;
	}

	function AddNewNode(parentNode: StoreGroupTree, name: string) {
		setLoading(true);
		const group: StoreGroup = {
			id: 0,
			name: name,
			parentStoreGroupId: parentNode.id,
			useRootGroup: true,
			ownerId: 0
		};

		dispatch(createStoreGroup(group)).then(() => {
			ReloadTree();
		});

	}

	function EditNode(parentNode: StoreGroupTree, name: string) {
		setLoading(true);
		let group: StoreGroup = {
			id: parentNode.id,
			name: name,
			parentStoreGroupId: parentNode.parentStoreGroupId,
			useRootGroup: parentNode.useRootGroup,
			ownerId: 0
		};
		dispatch(updateStoreGroup(group)).then(() => {
			ReloadTree();
		});

	}

	function DeleteNode(selectedNode: StoreGroupTree) {
		setLoading(true);
		if (currentStoreGroupId === selectedNode.id) {
			let rootNode: any = manageGroups.tree[0];
			dispatch(updateStoreGroupId(rootNode.id));
			dispatch(updateStoreGroupField({ key: "storeName", value: rootNode.name }));
		}
		dispatch(deleteStoreGroup({ selectedIds: [selectedNode.id] })).then(() => {
			//Success
			ReloadTree();
		});

	}

	function ReloadTree() {
		dispatch(getManageGroupsStoreTree()).then(() => {
			setLoading(false);
		});
	}

	function SelectNode(nodes: StoreGroupTree[], id: number) {
		nodes.forEach(n => {
			if (n.id === id) {
				setSelectedNode(n);
				return;
			}

			if (n.children && n.children.length > 0) {
				SelectNode(n.children, id);
			}
		})
	}

	function AddSelectedStores(saved: boolean, newStores?: Store[]) {
		if (saved && selectedNode && newStores) {
			let temp: any = [...selectedNode.stores];
			newStores.forEach((store) => {
				if (!temp.find((i: any) => i === store)) temp.push(store);
			});
			SaveStores(temp);
		}
	}

	function RemoveSelectedStores() {
		if (selectedNode) {
			let temp = [...selectedNode.stores];
			selectedStores.current.forEach((store) => {
				const index = temp.findIndex(i => i === store.id);
				if (index > -1) temp.splice(index, 1);
			})
			SaveStores(temp);
		}
	}

	function SaveStores(stores: number[]) {
		setLoading(true);
		if (selectedNode) {
			if (stores.length > 0) {
				dispatch(updateStoreGroupStores(stores.map(i => ({ storeId: i, storeGroupId: selectedNode.id })))).then(() => {
					ReloadTree();
				});
			}
			else {
				dispatch(deleteStoresByGroup([selectedNode.id])).then(() => {
					ReloadTree();
				});
			}
		} else {
			console.log("Error! How could we not find the match?");
			setLoading(false);
		}
	}

	return (
		<>
			<Title pageTitle="Store Groups" />
			<Row>
				<Col xs={4} className='pe-0'>
					<Card>
						<Card.Body>
							<Row>
								<Col xl={12} className='p-0'>
									<ListGroup>
										{RenderTree(manageGroups.tree, 0)}
									</ListGroup>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
				<Col xs={8}>
					<Card>
						<Card.Body className='ps-2'>
							<Row className="mb-0">
								<TreeStoresPopup
									treeStores={parentStores}
									show={storesPopup}
									handleClose={() => setStoresPopup(false)}
									saveSelectedRows={(saved: boolean, newStores?: Store[]) => {
										AddSelectedStores(saved, newStores);
										setStoresPopup(false);
									}} />
								<Table
									columns={columnsb}
									data={manageGroupsStores}
									pageSize={15}
									isSortable={true}
									pagination={true}
									isSelectable={true}
									isSearchable={true}
									addNewRow={!user.permissions.storeGroups.canCreate || selectedNode?.level === 0 ? undefined : () => setStoresPopup(true)}
									removeRows={!user.permissions.storeGroups.canDelete || manageGroups.tree.map((i: any) => i.id).includes(selectedNode?.id) ? undefined : RemoveSelectedStores}
									canDelete={true}
									selectedRows={(rows) => {
										const ss = stores.table.filter((i: any) => selectedNode?.stores.includes(i.id));
										let newSelection: Store[] = [];
										for (let index = 0; index < ss.length; index++) {
											const element = ss[index];
											if (rows.includes(index)) newSelection.push(element);
										}
										selectedStores.current = newSelection
									}}
								/>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<NewGroupPopup
				onClose={() => setShowNewPopup(false)}
				show={showNewPopup}
				heading='Add New Group'
				value=''
				onSave={(newValue: string) => {
					addEditNode && AddNewNode(addEditNode, newValue);
					setShowNewPopup(false);
				}}
			/>

			<NewGroupPopup
				onClose={() => setShowEditPopup(false)}
				show={showEditPopup}
				heading='Edit Group'
				value={selectedNode?.name ?? ''}
				onSave={(newValue: string) => {
					addEditNode && EditNode(addEditNode, newValue);
					setShowEditPopup(false);
				}}
			/>

			{popupMessage && (
				<Popup
					show={displayPopup}
					title={popupMessage.title}
					description={popupMessage.description}
					buttonText={popupMessage.buttonText}
					handleClose={() => popupMessage.cancel()}
					handleSubmit={() => {
						popupMessage.submit();
					}
					}
				/>
			)}

			{loading && (<div className='dimScreen'>
				<div className='dimScreenContents'>
					<Spinner className="m-2" color="primary" />
				</div>
			</div>)}
		</>
	);
}